import type { AxiosInstance, AxiosResponse } from 'axios';
import { keys } from '../constants/keys';
import { routes } from '../constants/routes';
import { persistQuery } from '../../../utils/helpers/persistQuery';
import { AddInError } from '../../../utils/addInError';
import type { ConfigurationResponse } from '../../../utils/types/api';

const CONFIG_TTL = 5 * 60 * 1000; // 5 minutes

const BODY_PREVIEW_LENGTH = 200;

const assertConfigurationResponse: (
  response: AxiosResponse<unknown>,
) => asserts response is AxiosResponse<ConfigurationResponse> = (response) => {
  type UnvalidatedBody = { data?: Record<string, unknown> } | null | undefined;
  const config = (response.data as UnvalidatedBody)?.data;

  if (
    typeof config?.v2 !== 'boolean' ||
    typeof config?.['secure-before-you-click'] !== 'boolean' ||
    typeof config?.['test-safely'] !== 'boolean'
  ) {
    const body = response.data;
    throw new AddInError('Unexpected configuration response shape', {
      status: response.status,
      body: typeof body === 'string' ? body.slice(0, BODY_PREVIEW_LENGTH) : body,
    });
  }
};

// Resolve the client lazily at fetch time: on-premises the query is enabled
// before initPhishedContainer's effect runs, so an eagerly-captured client
// would still be undefined.
export const configurationQueryOptions = (getClient: () => AxiosInstance, email: string) => ({
  queryKey: keys.configuration,
  queryFn: async (): Promise<AxiosResponse<ConfigurationResponse>> => {
    const response = await getClient().post<unknown>(routes.phished.configuration, { email });
    assertConfigurationResponse(response);

    return response;
  },
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  ...persistQuery(CONFIG_TTL),
});
